import { defineStore } from "pinia"
import { updateHeaderConfig } from "@/api/net/config.js"
import globalConfig from "@/config/config.js"
import apiUser from "@/api/api/apiUser.js"
import useHomeDataStore from "@/store/homeDataStore.js"
import { funComponentList } from "@/components/functionComp/index"
import { getRealityUser } from "@/api/api/apiCommon.js"
import useDriverStore from "@/store/driverStore.js"

import useDraftDataStore from "./create/draftDataStore.js"
import useGlobalUserStateStore from "./globalUserStateStore.js"
import useAbtestStore from "@/store/abtestStore.js"
import { closeAllDialog } from "@/components/basic/dialog/dialog.js"
import { closeAllActionDialog } from "@/components/functionCallComponent/action/action.js"
import { firebaseUtils } from "@/utils/analytic/firebaseUtils.js"

import { getCreditInfo, getSubscriptionStatus } from "@/api/api/apiCredit.js"
import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import message from "@/components/functionCallComponent/message/message.js"
import { getLatestUtmSourceFromCookie, getLatestUtmSource } from "@/api/net/config.js"
import { mapBrowserLanguage } from "@/i18n/i18n.js"
import { inMurekaApp, hasCookie, isDev, languagelist, isMobileDevice } from "@/utils/tools.js"
import { jsBridge } from "@/utils/jsBridge.js"
import { setCookie, getCookie, deleteCookie } from "@/utils/cookieUtils"
import useModelDataStore from "@/store/model/modelDataStore.js"
import useTtsVocalMusicStore from "@/store/tts/ttsVocalMusicStore.js"
import { useTtsHomeStore } from "@/store/tts/ttsHome.js"
import { queryCertification, getPendingInvoice } from "@/api/api/apiCredit.js"


const getLocalData = () => {
  function getVipLevelCode(userInfo) {
    const level = userInfo?.user?.vip_level_name
    if (level === "basic") return "1"
    if (level === "pro") return "2"
    return "0"
  }
  const cookieData = getCookie(globalConfig.localStorageGlobalKey) || ""
  if (!cookieData) return {}
  const { userInfo, authData, config, isLogin, k_sso_token, appLanguage, ..._data } = JSON.parse(
    decodeURIComponent(cookieData)
  )
  const language = mapBrowserLanguage(appLanguage) || ""
  if (!import.meta.env.SSR) {
    import("sa-sdk-javascript").then((module) => {
      let sa = module.default || module
      if (!isLogin) {
        sa.logout()
      } else {
        sa.login(userInfo?.user?.user_id?.toString())
        sa.register({
          user_status: getVipLevelCode(userInfo) || "",
          credits: userInfo?.credits?.toString() || "",
          // device_language: language,
        })
      }
    })
  }
  if (!isLogin) {
    return {}
  }

  if (authData) {
    const data = {
      k_sso_token,
      // appLanguage: language,
      userInfo,
      isLogin,
      authData,
      config,
      ..._data,
    }
    return data
  }
  return {}
}

const useUserDataStore = defineStore({
  id: "userDataStore",
  state: () => {
    const driverStore = useDriverStore()
    const localData = getLocalData()
    const data = {
      credits: 0,
      skyworkInfo: {
        isApp: true,
        isCheckLogin: false,
      },
      appInfo: {
        name: "SkyMusic.Ai",
      },
      isLogin: false, // 是否登陆
      authData: {
        // user_id: 100, // 临时使用
        // oauth_key: "788vaWdtOua0kIuClIkNWMNlcTXSaLNW"
        // app_user_id: "u-6a41a76fa9416d9106b7d26498ae03"
      },
      userInfo: {
        // 全局用户信息
      },
      config: {
        // 全局配置
      },
      appLanguage: "",
      k_sso_token: "",
      userRelData: null,
      bannerList: null,
      activityConfig: null,
      creditInfo: {},
      hasInit: false,
      lastCheckedTime: "",
      showLastCheckModal: false,
      channel: "",
      isCancelStatus: false,
      countdown: 0,
      countdownTimer: null,
      free_tts_duration: 0,
      certificationState: 0, // 认证状态
      billList: [], // 发票列表
      billListLastId: '', // 发票列表分页标识
      billListHasMore: true, // 发票列表是否还有更多数据
      certificationDetail: {}, // 认证详情
      ...localData,
    }
    data.authData && updateHeaderConfig(data.authData)
    driverStore.init(data.isLogin)
    return data
  },
  actions: {
    initCookie(cookie) {
      if (!cookie) return
      const cookieData = getCookie(globalConfig.localStorageGlobalKey, cookie) || "{}"
      const { userInfo, authData, config, isLogin, k_sso_token, appLanguage, ..._data } =
        JSON.parse(decodeURIComponent(cookieData))
      if (isLogin && authData) {
        // 彻底覆盖 store 字段，避免残留
        this.k_sso_token = k_sso_token
        // this.appLanguage = mapBrowserLanguage(appLanguage)
        this.userInfo = userInfo
        this.isLogin = isLogin
        this.authData = authData
        this.config = config
        // 合并其它字段
        Object.assign(this, _data)
      }
    },
    async init() {
      // 获取url参数
      try {
        // 判断是否在 MurekaApp 且未获取用户 ID
        // if (inMurekaApp()) {
        //   await this.applogin()
        // }
        if (!import.meta.env.SSR) {
          import("sa-sdk-javascript").then((module) => {
            let sa = module.default || module
            sa.register({
              device_language: this.appLanguage,
            })
          })
        }
        const userInfo = await this.getUserInfo()
        if (userInfo.code !== 401) {
          await this.getUserRealData()
        }
        // 如果需要调用 getConfig，可以在此处解注释
        // await this.getConfig();
      } catch (err) {
        // 全局捕获异常
        console.error("Error during initialization:", err)
        throw err // 如果需要继续向外抛出错误，可以保留此行
      }
    },
    getUserRealData() {
      return new Promise((resolve, reject) => {
        getRealityUser({}).then((res) => {
          if (res.code == 200) {
            this.hasInit = true
            this.setUserRelData(res.data)
          }
        })
      })
    },
    // 删除cookie的函数
    deleteSessionCookie() {
      console.log("清除成功")
      const cookieName = `skm_session${isDev() ? "_test" : ""}`
      const domain = !import.meta.env.SSR ? window.location.hostname : ""

      // 两种方式确保删除
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`
    },
    clearAll(isClearLocalData = true) {
      const draftDataStore = useDraftDataStore()
      const createLayoutStore = useCreateLayoutStore()
      const abtestStore = useAbtestStore()
      const ttsHomeStore = useTtsHomeStore()
      deleteCookie(globalConfig.localStorageGlobalKey)
      this.authData = {}
      this.userInfo = {}
      this.config = {}
      this.hasInit = false
      abtestStore.createSubscribeGuideBarType = ""
      createLayoutStore.clearData()
      ttsHomeStore.clearAll()
      this.clearOther()
      this.updateLoginStatus(false)
      this.deleteSessionCookie()
      localStorage.removeItem("show_coupon")
      useTtsVocalMusicStore().clearAllReferSelectData()
      useGlobalUserStateStore().reset()
      if (isClearLocalData) {
        draftDataStore.clearData()
      }
    },
    clearOther() {
      updateHeaderConfig(this.authData)
    },
    getUserInfo() {
      const abtestStore = useAbtestStore()
      return new Promise((resolve, reject) => {
        apiUser.userGetInfo().then((res) => {
          const { data, code, msg } = res
          if (code == 200) {
            this.credits = data.credits
            this.userInfo = data ?? {}
            this.free_tts_duration = data.free_tts_duration
            abtestStore.initCreateSubscribeType()
            if (!this.authData?.user_id) {
              this.authData = {
                user_id: data?.userInfo?.user?.user_id,
              }
            }
            if (this.userInfo?.discount?.countdown_second) {
              this.startCountdown(this.userInfo?.discount?.countdown_second)
            }
            this.updateLoginStatus(true)
            if (!import.meta.env.SSR) {
              this.checkSubscriptionStatus()
            }
            useGlobalUserStateStore().queryUserState()
            resolve(data)
            // this.getBanerConfig()
          } else {
            console.log("oneTap")
            this.clearAll()
            this.googleOneTapLogin()
            reject({})
          }
        })
      })
    },
    async googleOneTapLogin() {
      const globalProperties =
        window?.$getVueCurrentInstance?.().appContext?.config?.globalProperties
      if (globalConfig.appname === "MurekaCn") {
        return
      }
      if (this.isLogin || isMobileDevice()) {
        return
      }
      try {
        const { googleOneTap } = await import("vue3-google-login")
        const response = await googleOneTap()
        await this.threeLogin(response?.credential, true)
        globalProperties?.logClickEvent?.("auto_google_login")
        const loginDom = document.querySelector(".third-login-dom")
        if (loginDom) loginDom.remove()
      } catch (error) {
        console.log("Handle the error", error)
      }
    },
    async checkPayState() {
      console.log("正在检查支付状态...")
      const { data, code } = await getSubscriptionStatus()
      if (code == 200 && data.items) {
        data.items?.forEach((item) => {
          if (item?.origin_status == 6) {
            this.showLastCheckModal = true
            this.channel = item.channel
          }
        })
      }
      const now = new Date().toLocaleString()
      this.lastCheckedTime = now // 更新最后一次检查时间
      localStorage.setItem("lastCheckedDate", now) // 保存到 localStorage
    },

    async checkCancelState() {
      const { data, code } = await getSubscriptionStatus()
      if (code == 200 && data.items) {
        data.items?.forEach((item) => {
          if (item?.status == 4) {
            this.isCancelStatus = true
          }
        })
      }
    },

    changeCancelState(flag) {
      this.isCancelStatus = true
    },

    checkSubscriptionStatus() {
      const lastCheckedDate = localStorage.getItem("lastCheckedDate") ?? ""
      const now = new Date()
      const lastCheckedDateObj = lastCheckedDate ? new Date(lastCheckedDate) : null

      if (!lastCheckedDate || lastCheckedDateObj.toDateString() !== now.toDateString()) {
        this.checkPayState()
      } else {
        this.lastCheckedTime = lastCheckedDate // 显示上次检查的时间
      }
    },
    setDisCount(discount) {
      this.userInfo = {
        ...this.userInfo,
        discount: discount,
      }
    },
    async applogin() {
      if (jsBridge.inMurekaApp) {
        const { user_id, token } = await jsBridge("state.session")
        console.log("view.saveFile res", user_id, token, this?.authData?.user_id)
        if (user_id && this?.authData?.user_id == user_id) {
          console.log("User is already logged in.")
          return
        }

        if (token) {
          await this.appLoginState(token)
        } else {
          this.clearAll()
        }
      }

      // if (!user_id || this?.authData?.user_id != user_id) {
      //   if (token){
      //     this.appLoginState(token)
      //   } else {
      //     this.clearAll()
      //   }
      // }
      // this.appLoginState('Mx4jCCNSkK8NgZ7197LfQNjnbnFzmQcE');
    },
    getConfig() {
      return new Promise((resolve, reject) => {
        apiUser
          .userConfig(this.authData.user_id)
          .then((res) => {
            const { data, code, msg } = res
            if (code == "200") {
              this.config = data ?? {}
              // this.appLanguage = data.app_language ?? "en"
              resolve()
            } else {
              reject({})
            }
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    getAll() {
      const { isLogin, authData, userInfo, config, appLanguage, k_sso_token } = this
      return {
        isLogin,
        authData,
        userInfo,
        config,
        appLanguage,
        k_sso_token,
      }
    },
    setCredits(credits) {
      this.credits = credits
    },
    updateLoginStatus(isLogin) {
      this.isLogin = isLogin
      setCookie(globalConfig.localStorageGlobalKey, JSON.stringify(this.getAll()))
      if (isLogin) {
        firebaseUtils.logLoginEvent(this?.authData?.user_id ?? 0)
      } else {
        if (!import.meta.env.SSR) {
          import("sa-sdk-javascript").then((module) => {
            let sa = module.default || module
            sa.logout()
          })
        }
        const homeDataStore = useHomeDataStore()
        homeDataStore.abortSse()
      }
    },
    updateUserInfo(userInfo) {
      this.userInfo = userInfo
      setCookie(globalConfig.localStorageGlobalKey, JSON.stringify(this.getAll()))
    },
    upDataAuthData(authData) {
      this.authData = authData
      updateHeaderConfig(authData)
      setCookie(globalConfig.localStorageGlobalKey, JSON.stringify(this.getAll()))
      this.init()
    },
    getActivitySource() {
      const utm_source = getLatestUtmSource()
      if (utm_source) {
        return "activity_kol_link"
      }
      return localStorage.getItem("activity_code") ?? ""
    },
    inviteCodePoint(invite) {
      apiUser.inviteCodePoint({ code: invite }).then(() => {
        localStorage.removeItem("invite_code")
        localStorage.removeItem("activity_code")
        const newUrl = this.removeURLParameter(
          !import.meta.env.SSR ? window.location.href : "",
          "invite_code"
        )
        !import.meta.env.SSR ? window.history.replaceState(null, "", newUrl) : ""
      })
    },
    getSignupSource(signupSource) {
      if (signupSource) {
        return signupSource
      }
      const signup_source = sessionStorage.getItem("signup_source") ?? ""
      if (signup_source) {
        sessionStorage.removeItem("signup_source")
        return signup_source
      }
      const invite = localStorage.getItem("invite_code") ?? ""
      if (invite) {
        return "invite_activity"
      }
      const createLayoutStore = useCreateLayoutStore()
      const { createEditMode } = storeToRefs(createLayoutStore)
      const pathname = !import.meta.env.SSR ? window.location.pathname : ""
      if (pathname == "/") {
        return "home_basic"
      } else if (pathname == "/create") {
        if (createEditMode.value == "easy") {
          return "create_basic"
        } else {
          return "create_advance"
        }
      } else if (pathname == "/special-christmas-gift" || pathname == "/gift-detail") {
        return "activity"
      } else {
        return "other"
      }
    },
    removeURLParameter(url, parameter) {
      const urlObj = new URL(url)
      urlObj.searchParams.delete(parameter)
      return urlObj.toString()
    },
    point() {
      apiUser.userPoint().then(() => {})
    },
    checkLoginStatus(params = {}, callback) {
      return new Promise((resolve) => {
        if (this.isLogin) {
          resolve(this.isLogin)
          return
        }

        closeAllDialog()
        closeAllActionDialog()

        if (inMurekaApp()) {
          jsBridge("view.login")
          resolve()
          return
        }

        const commonOptions = {
          rightType: params?.rightType,
          create_type: params?.create_type,
          source: params?.source,
          handleOk: () => callback?.(this.isLogin),
          handleCancel: () => callback?.(false),
        }

        console.log("globalConfig.appname", globalConfig.appname)
        if (globalConfig.appname === "MurekaCn") {
          funComponentList.LogInCn(commonOptions)
        } else {
          funComponentList.ThirdLogin({
            ...commonOptions,
            signup_source: params?.signup_source ?? "",
          })
        }

        resolve()
      })
    },
    setUserRelData(data) {
      this.userRelData = Object.assign({}, this.userRelData ?? {}, data ?? {})
    },
    switchLanguage(curLanguage) {
      console.log("uselang切换语言", curLanguage)
      const lastLang = this.appLanguage
      if (curLanguage == lastLang) return
      // this.appLanguage = curLanguage
      // const draftDataStore = useDraftDataStore()
      // draftDataStore.clearAll(true)
      // setCookie(globalConfig.localStorageGlobalKey, JSON.stringify(this.getAll()))
      // if (!import.meta.env.SSR) {
      //   import("sa-sdk-javascript").then((module) => {
      //     let sa = module.default || module
      //     sa.register({
      //       device_language: curLanguage,
      //     })
      //   })
      // }
      let newUrl
      if (lastLang == "en") {
        if (curLanguage !== "en") {
          const url = new URL(window.location.href)
          let path = url?.pathname
          newUrl = `${url?.origin}${`/${curLanguage}`}${path}${url?.search}`
        }
      } else {
        if (curLanguage == "en") {
          const url = new URL(window.location.href)
          let path2 = url.pathname
          const langExp = new RegExp(`/(${languagelist.join("|")})(/|$)`)
          const newpath = path2.replace(langExp, "/")
          newUrl = `${url.origin}${newpath}${url?.search}`
        } else {
          const langExp = new RegExp(`/(${languagelist.join("|")})(?=/|$)`)
          newUrl = window.location.href.replace(langExp, `/${curLanguage}`)
        }
      }
      window.location.href = newUrl
      // if (lastLang == 'en'){
      //   window.location.href = window.location.href.replace(window.location.origin, window.location.origin + (curLanguage!='en' ? `/${curLanguage}` : ""))
      // } else {
      //   window.location.href = window.location.href.replace(langExp, curLanguage!='en' ? `/${curLanguage}` : "")
      // }
    },
    getBanerConfig() {
      return new Promise((resolve, reject) => {
        apiUser
          .getBanerConfig({})
          .then((res) => {
            const { data, code, msg } = res
            if (code == "200") {
              this.bannerList = data.list ?? []
              this.activityConfig = data.activity_config ?? {}
              resolve()
            } else {
              reject({})
            }
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    refreshUserAccount(isAfterCreated) {
      const modelDataStore = useModelDataStore()
      return new Promise((resolve, reject) => {
        getCreditInfo()
          .then((res) => {
            if (res.code == 200) {
              const { data } = res
              this.credits = data?.gold || 0
              useTtsHomeStore().free_tts_duration = data?.tts_free_duration || 0

              resolve(this.credits)
              if (isAfterCreated) {
                const Router =
                  window?.$getVueCurrentInstance?.().appContext?.config?.globalProperties?.$router
                modelDataStore.refreshingModelState({
                  model: ["create", "createIndex"].includes(Router.currentRoute.value.name) ? 0 : 3,
                })
              }
            } else {
              reject(res)
            }
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    setCreditsInfo(creditsInfo) {
      this.creditInfo = creditsInfo
      this.credits = creditsInfo?.gold || 0
    },
    updateAuthData(_data) {
      this.upDataAuthData(_data.auth)
      this.updateLoginStatus(true)
    },
    async threeLogin(x_auth_token, is_auto = false, signup_source, mode) {
      return new Promise(async (resolve, reject) => {
        const { data, code, msg } = await apiUser.userLogin({
          mode: mode ?? "google",
          ...(is_auto ? { extra_token: "auto_login" } : {}),
          token: x_auth_token,
          source: getLatestUtmSourceFromCookie() ?? "",
          signup_source: this.getSignupSource(signup_source),
          activity_source: this.getActivitySource(),
        })
        if (code === 200) {
          console.log("第三方登录成功")
          this.updateAuthData(data)
          const createLayoutStore = useCreateLayoutStore()
          createLayoutStore.toggleCreateEditOpenState(true)
          if (data?.is_new) {
            this.invite()
          }
          resolve()
        } else if (code == 6401) {
          message.error({
            maskClosable: true,
            position: "top",
            content: "Deleted accounts cannot be registered within 14 days.",
          })
          resolve()
        } else {
          message.error({
            maskClosable: true,
            position: "top",
            content: msg,
          })
          resolve()
        }
      })
    },
    async appLoginState(x_auth_token) {
      return new Promise(async (resolve, reject) => {
        const { data, code, msg } = await apiUser.userLogin({
          mode: "self",
          token: x_auth_token,
          source: getLatestUtmSourceFromCookie() ?? "",
          signup_source: this.getSignupSource(),
        })
        if (code === 200) {
          console.log("第三方登录成功")
          this.updateAuthData(data)
          const createLayoutStore = useCreateLayoutStore()
          createLayoutStore.toggleCreateEditOpenState(true)
          resolve()
        } else if (code == 6401) {
          message.error({
            maskClosable: true,
            position: "top",
            content: "Deleted accounts cannot be registered within 14 days.",
          })
          resolve()
        } else {
          message.error({
            maskClosable: true,
            position: "top",
            content: msg,
          })
          resolve()
        }
      })
    },
    invite() {
      const invite = localStorage.getItem("invite_code") ?? ""
      if (invite) {
        this.inviteCodePoint(invite)
      }
    },
    // 开始倒计时
    startCountdown(seconds) {
      console.log("倒计时seconds:", seconds)

      this.stopCountdown()
      this.countdown = seconds

      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
          console.log("倒计时:", this.countdown)
        } else {
          this.stopCountdown()
        }
      }, 1000)
    },

    // 停止倒计时
    stopCountdown() {
      if (this.countdownTimer) {
        this.countdown = 0
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    // 重置倒计时
    resetCountdown() {
      this.stopCountdown()
      this.countdown = 0
    },
    // 获取是否回流弹窗展示
    getUesrRewardsShow() {
      apiUser.giveCreditsExperiment().then((res) => {
        if (res.code == 200) {
          this.isShowReturenReward = res?.data?.pop
          if (this.isShowReturenReward) {
            const closeTimer = setTimeout(() => {
              clearTimeout(closeTimer)
              this.isShowReturenReward = false
            }, 3000)
          }
        }
      })
    },

    changeRewardsShowState() {
      this.isShowReturenReward = false
    },
    // 查询认证状态
    getUserCertificationState() {
      return new Promise((resolve) => {
        queryCertification().then(res => {
          if (res.code == 200) {
            const { state, detail } = res.data
            this.certificationDetail = detail || {};
            this.certificationState = state || 0
            resolve()
          }
        })
      })
    },
    async getUserBillList(query = {}) {
      const requestQuery = {
        ...query,
        size: 1000,
        last_id: query.last_id !== undefined ? query.last_id : this.billListLastId
      };
      
      const res = await getPendingInvoice(requestQuery)
      if (res.code == 200) {
        if (requestQuery.last_id === 0) {
          // 第一页数据，替换列表
          this.billList = res.data?.list || [];
        } else {
          // 后续页数据，追加到列表
          this.billList = [...this.billList, ...(res.data?.list || [])];
        }
        
        // 更新分页状态
        this.billListLastId = res.data?.last_id || 0;
        this.billListHasMore = res.data?.last_id !== 0;
        
        return {
          list: res.data?.list || [],
          last_id: res.data?.last_id || 0
        };
      }
      return {
        list: [],
        last_id: 0
      };
    }
  },
  getters: {
    getAuthData() {
      return this.authData
    },
    getAppInfo() {
      return this.appInfo
    },
  },
})
export default useUserDataStore
